import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  fetchCasesApi,
  fetchStatutesApi,
} from "../../../services/api/bookmarksApi";
import { toast } from "react-toastify";
import {
  getLawyersApi,
  getClientsApi,
  getCasesApi,
  getCaseDocumentsApi,
  addCommentApi,
} from "../../../services/api/caseManagementApi";
import { getUsersEmailsApi } from "../../../services/api/caseManagementApi";

export const CMSContext = createContext();

export const CMSProvider = ({ children }) => {
  const [data, setData] = useState(null);
  const [fetchingRefCase, setFetchingRefCase] = useState(false);
  const [fetchingRefStatute, setFetchingRefStatute] = useState(false);
  const [refCases, setRefCases] = useState([]);
  const [refStatute, setRefStatute] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedCase, setSelectedCase] = useState(null);
  const [advocates, setAdvocates] = useState([]);
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [isHistoryClicked, setIsHistoryClicked] = useState(true);
  const [fetchCaseLoading, setFetchCaseLoading] = useState(false);
  const [fetchCaseDocLoading, setFetchCaseDocLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const fetchAdvocatesInFlightRef = useRef(null);
  const fetchClientsInFlightRef = useRef(null);
  const fetchCasesInFlightRef = useRef(null);
  const fetchDocumentsInFlightRef = useRef(null);
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const ownerId = userInfo._id;
  const [documents, setDocuments] = useState([]);
  const [taskRefreshKey, setTaskRefreshKey] = useState(0);

  const triggerTaskRefresh = useCallback(() => {
    setTaskRefreshKey((k) => k + 1);
  }, []);

  const fetchAdvocates = useCallback(async () => {
    try {
      if (fetchAdvocatesInFlightRef.current) {
        return fetchAdvocatesInFlightRef.current;
      }
      fetchAdvocatesInFlightRef.current = true;
      const response = await getLawyersApi(ownerId);
      const data = response.data;
      setAdvocates(
        data?.filter((advocate) => advocate?.userId !== ownerId) || [],
      );
    } catch (error) {
      console.error("Error fetching advocates:", error);
    } finally {
      fetchAdvocatesInFlightRef.current = false;
    }
  }, [ownerId]);

  const fetchClients = useCallback(async () => {
    try {
      if (fetchClientsInFlightRef.current) {
        return fetchClientsInFlightRef.current;
      }
      fetchClientsInFlightRef.current = true;
      const response = await getClientsApi(ownerId);
      const data = response.data;
      setClients(data || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      fetchClientsInFlightRef.current = false;
    }
  }, [ownerId]);

  const fetchCases = useCallback(
    async (selectedCaseId, force = false) => {
      if (!force && !selectedCaseId && cases && cases.length > 0) {
        setCases(cases);
        return Promise.resolve(cases);
      }

      if (fetchCasesInFlightRef.current) {
        return fetchCasesInFlightRef.current;
      }

      const request = (async () => {
        try {
          setFetchCaseLoading(true);
          const response = await getCasesApi(ownerId);
          const data = response.data;
          setCases(data);
          const sortedCases = [...data].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
          );
          const nextSelectedCase = selectedCaseId
            ? sortedCases.find((caseItem) => caseItem._id === selectedCaseId) ||
              sortedCases[0]
            : sortedCases[0];

          setSelectedCase(nextSelectedCase);
        } catch (error) {
          console.error("Error fetching cases:", error);
        } finally {
          setFetchCaseLoading(false);
        }
      })();

      fetchCasesInFlightRef.current = request;
      try {
        return await request;
      } finally {
        fetchCasesInFlightRef.current = null;
      }
    },
    [ownerId, cases],
  );

  const fetchUsers = useCallback(async () => {
    try {
      const response = await getUsersEmailsApi();
      const data = response.data;
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [ownerId]);

  const fetchReferenceCases = useCallback(async () => {
    if (!selectedCase) return;
    setRefCases([]);
    setFetchingRefCase(true);
    try {
      const caseIds =
        selectedCase?.caseReferences?.length > 0
          ? selectedCase.caseReferences
              .map((caseItem) => `Bookmark_ID eq ${caseItem}`)
              .join(" or ")
          : "";

      if (!caseIds) {
        setFetchingRefCase(false);
        return;
      }

      const searchData = await fetchCasesApi(caseIds);
      if (searchData) {
        setRefCases(searchData);
      }
    } catch (error) {
      console.error("Error fetching cases:", error);
    } finally {
      setFetchingRefCase(false);
    }
  }, [selectedCase]);

  const fetchReferenceStatute = useCallback(async () => {
    if (!selectedCase) return;

    setFetchingRefStatute(true);
    try {
      const statuteIDs =
        selectedCase?.statuteReferences?.length > 0
          ? selectedCase.statuteReferences
              .map((caseItem) => `Bookmark_ID eq ${caseItem}`)
              .join(" or ")
          : "";

      if (!statuteIDs) {
        setFetchingRefStatute(false);
        return;
      }

      const searchData = await fetchStatutesApi(statuteIDs);
      if (searchData) {
        setRefStatute(searchData);
      }
    } catch (error) {
      console.error("Error fetching cases:", error);
    } finally {
      setFetchingRefStatute(false);
    }
  }, [selectedCase]);

  const fetchDocuments = useCallback(async () => {
    if (!selectedCase?._id) return;

    const requestKey = selectedCase._id;
    if (
      fetchDocumentsInFlightRef.current &&
      fetchDocumentsInFlightRef.current.key === requestKey
    ) {
      return fetchDocumentsInFlightRef.current.promise;
    }

    const request = (async () => {
      setFetchCaseDocLoading(true);
      try {
        const response = await getCaseDocumentsApi(selectedCase._id);

        if (response.status === 200) {
          const docs = response.data.documents || [];
          setDocuments(docs);
          return docs;
        }
        return [];
      } catch (error) {
        console.error("Failed to fetch documents:", error);
        toast.error(
          error.response?.data?.message || "Failed to load documents",
          { variant: "error" },
        );
        return [];
      } finally {
        setFetchCaseDocLoading(false);
      }
    })();

    fetchDocumentsInFlightRef.current = { key: requestKey, promise: request };
    try {
      return await request;
    } finally {
      fetchDocumentsInFlightRef.current = null;
    }
  }, [selectedCase?._id]);

  const handleAddComment = async (commentText) => {
    try {
      const response = await addCommentApi(selectedCase?._id, selectedDocument?._id, {
        commentedBy: ownerId,
        commentedByName: userInfo?.name || null,
        commentText,
      });

      if (response.status === 201) {
        setSelectedDocument((prev) => ({
          ...prev,
          comments: [
            ...prev.comments,
            {
              ...(response.data.comment || {}),
              commentedBy: ownerId,
              commentedByName: userInfo?.name || null,
              commentText,
              createdAt: response.data.comment?.createdAt || new Date().toISOString(),
            },
          ],
        }));
        fetchDocuments();
      }
    } catch (error) {
      console.error("Failed to add comment:", error);
      toast.error(error.response?.data?.message || "Failed to add comment");
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Sync selectedDocument from the refreshed documents array so comments and
  // other fields update automatically without requiring the sidebar to be closed/reopened.
  useEffect(() => {
    if (!selectedDocument?._id || !documents?.length) return;
    const updated = documents.find((d) => d._id === selectedDocument._id);
    if (updated) setSelectedDocument(updated);
  }, [documents]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchReferenceCases();
    fetchReferenceStatute();
  }, [fetchReferenceCases, fetchReferenceStatute]);

  return (
    <CMSContext.Provider
      value={{
        data,
        setData,
        documents,
        handleAddComment,
        setDocuments,
        selectedCase,
        setSelectedCase,
        currentTab,
        setCurrentTab,
        advocates,
        setAdvocates,
        clients,
        setClients,
        fetchClients,
        fetchAdvocates,
        cases,
        setCases,
        fetchCaseLoading,
        setFetchCaseLoading,
        fetchCases,
        isHistoryClicked,
        setIsHistoryClicked,
        fetchUsers,
        users,
        fetchReferenceCases,
        fetchReferenceStatute,
        fetchingRefCase,
        fetchingRefStatute,
        refCases,
        refStatute,
        setRefCases,
        setRefStatute,
        fetchCaseDocLoading,
        fetchDocuments,
        selectedDocument,
        setSelectedDocument,
        taskRefreshKey,
        triggerTaskRefresh,
      }}
    >
      {children}
    </CMSContext.Provider>
  );
};
